// TODO: SER-508 - Fix lint errors and remove eslint-disable
/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { AccountInfo } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { DriveItem } from '@microsoft/microsoft-graph-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { MICROSOFT_INTEGRATION_CLIENT_ID } from '@/common/constants';
import {
  loginRequest,
  MSFT_FILE_PICKER_CANCEL,
  MSFT_FILE_PICKER_ERROR,
  MSFT_FILE_PICKER_PENDING,
  MSFT_FILE_PICKER_SUCCESS,
  oneDrivePickerScopes,
} from '@/pages/home/content/integration/microsoft/MSALConfig';
import { mapMicrosoftGraphDriveItemToMicrosoftFile } from '@/pages/home/content/integration/microsoft/types';

const PARENT_ORIGIN = sessionStorage.getItem('MARVERI_PARENT_ORIGIN') || '*';

export const MSALAuth = () => {
  const location = useLocation();
  const { instance } = useMsal();
  const msalInstanceRef = useRef(false);
  const [accessToken, setAccessToken] = useState<string | undefined>(undefined);

  /**
   * Acquires an access token for the selected account
   * Acquired access token is used to authenticate the user in the OneDrive SDK
   */
  const acquireAccessToken = useCallback(
    async (accountUsername?: string): Promise<string> => {
      let loginHint = accountUsername;
      if (!loginHint) {
        const activeAccount = instance.getActiveAccount();
        if (!activeAccount) {
          throw new Error('[MSAL] No active account found, token acquisition failed');
        }
        loginHint = activeAccount.username;
        console.log('[MSAL] No account username provided, using active account:', loginHint);
      }

      console.log('[MSAL] Acquiring access token for account:', loginHint);

      // Note: for explicit consent each time, use popup authentication
      // Option to try silent authentication first, and if it fails, use popup authentication for better user experience
      const authResult = await instance.acquireTokenPopup({
        scopes: oneDrivePickerScopes,
        loginHint,
        prompt: 'consent', // Force consent UI
      });
      console.log(
        '[MSAL] Access token successfully acquired:',
        authResult.accessToken.slice(0, 10),
      );

      setAccessToken(authResult.accessToken);
      return authResult.accessToken;
    },
    [instance],
  );

  /**
   * Loads the OneDrive SDK
   * @returns A promise that resolves when the SDK is loaded
   */
  const loadOneDriveSDK = useCallback((): Promise<void> => {
    return new Promise((resolve, reject) => {
      if ((window as any).OneDrive) {
        console.log('[OneDrive SDK] OneDrive SDK already loaded');
        resolve();
        return;
      }

      const tryLoadScript = (src: string): void => {
        const script = document.createElement('script');
        script.src = src;
        script.async = true;

        const cleanup = () => {
          script.onload = null;
          script.onerror = null;
          if (script.parentNode) {
            script.parentNode.removeChild(script);
          }
        };

        script.onload = () => {
          console.log(`[OneDrive SDK] OneDrive SDK loaded successfully from ${src}`);
          cleanup();
          resolve();
        };

        script.onerror = (error) => {
          console.error(`[OneDrive SDK] OneDrive SDK load failed from ${src}:`, error);
          cleanup();
          reject(new Error(`[OneDrive SDK] Failed to load OneDrive SDK ${src}`));
        };

        document.head.appendChild(script);

        // Add timeout
        setTimeout(() => {
          if (script.parentNode && !script.onload) {
            console.error(`[OneDrive SDK] OneDrive SDK load timeout from ${src}`);
            cleanup();
            reject(new Error('[OneDrive SDK] Failed to load OneDrive SDK due to timeout'));
          }
        }, 5000);
      };

      // Try loading SDK 7.2
      // Note: v8.0 is not stable yet
      tryLoadScript('https://js.live.net/v7.2/OneDrive.js');
    });
  }, []);

  /**
   * Opens the OneDrive file picker
   */
  const openOneDriveFilePicker = useCallback(
    async (account: AccountInfo) => {
      try {
        // Make sure account and accessToken are set
        if (!account) throw new Error('[OneDrive SDK] User not logged in');

        // Always fetch a fresh token pair for this picker session
        const pickerAccessToken = await acquireAccessToken(account.username);

        // Comprehensive guard to prevent OneDrive SDK from receiving non-string messages
        // This prevents the "e.data.indexOf is not a function" error
        const MS_ORIGINS = [
          'live.net',
          'onedrive.live.com',
          'sharepoint.com',
          'officeapps.live.com',
          'office.com',
        ]; // filter out non-Microsoft origins (Sentry, LaunchDarkly, etc.)
        const filterNonMicrosoftMessages = (ev: MessageEvent) => {
          // Let Microsoft’s own frames talk, block every other *object* payload
          if (typeof ev.data !== 'string' && !MS_ORIGINS.some((d) => ev.origin.includes(d))) {
            console.info('[OneDrive SDK] blocked non-MS message:', ev.origin);
            ev.stopImmediatePropagation();
          }
        };
        window.addEventListener('message', filterNonMicrosoftMessages, true);

        // Load OneDrive SDK
        console.log('[OneDrive SDK] Loading OneDrive File Picker SDK...');
        await loadOneDriveSDK();

        // Only include loginHint if there is an account and it has a username
        // Note for Account lifecycle:
        // 1. MSAL Provider initializes and checks cache
        // 2. accounts array populated from cache or after login
        // 3. Silent auth attempted if accounts exist
        // 4. Interactive auth (popup) if silent fails
        //   - Silent Auth: instance.acquireTokenSilent({ account: accounts[0] })
        //   - Login Hint: Provides username to skip account selection
        //   - Single Account: Uses accounts[0] (first account is active)
        //   - accounts comes from useMsal() hook from @azure/msal-react
        //   - Auto-populated by MSAL when users authenticate or from cached tokens
        //   - Array of AccountInfo objects with properties: homeAccountId, username, name, tenantId, etc.
        // Note for OneDrive v8: the access token is not required. Sdk obtain its token under the hood for the delegated permissions/scopes

        const advanced = {
          // Currently not specifying redirectUri to use default config
          // If error occurs, revert to specifying redirectUri: ex) redirectUri: window.location.href, (must match the redirectUri in the app registration and current window URL - msal/auth)
          loginHint: account.username, // optional
          accessToken: pickerAccessToken, // v7.2 needs the string TODO: Remove this when v8.0 is stable
          // scope: oneDrivePickerScopesStr, // Currently not specifying to use default scope. TODO: test with narrower scope using oneDrivePickerScopesStr
          debug: true, // TODO: Remove if verbose
          queryParameters:
            'select=id,name,webUrl,size,createdDateTime,lastModifiedDateTime,' +
            'file,folder,parentReference',
        };
        console.debug('[OneDrive SDK] SDK advanced configuration:', advanced);

        try {
          sessionStorage.setItem('MARVERI_PARENT_ORIGIN', window.location.origin);

          // Add debugging to check if this is a popup environment issue
          console.debug('[OneDrive SDK] Window debugging info:', {
            isPopup: window.opener !== null,
            hasParent: window.parent !== window,
            windowName: window.name,
            referrer: document.referrer,
            userAgent: navigator.userAgent.substring(0, 100),
          });

          console.log('[OneDrive SDK] Opening OneDrive file picker...');
          // https://learn.microsoft.com/en-us/onedrive/developer/rest-api/getting-started/msa-oauth?view=odsp-graph-online
          (window as any).OneDrive.open({
            clientId: MICROSOFT_INTEGRATION_CLIENT_ID,
            action: 'query', // TODO: test query vs pick
            multiSelect: true,
            advanced,

            success: (selection: any) => {
              console.log('[OneDrive SDK] File selection successful:', selection);

              // Map OneDrive response to MicrosoftFile interface
              const selectedFiles = (selection.value ?? []).map((item: DriveItem) =>
                mapMicrosoftGraphDriveItemToMicrosoftFile(item),
              );

              window.opener?.postMessage(
                JSON.stringify({
                  type: MSFT_FILE_PICKER_SUCCESS,
                  selectedFiles,
                  email: account.username,
                  accessToken: pickerAccessToken,
                  reason: 'file picker success',
                }),
                PARENT_ORIGIN,
              );
            },
            cancel: () => {
              console.log('[OneDrive SDK] File picker cancelled:', {
                timestamp: new Date().toISOString(),
                windowLocation: window.location.href,
                hasAccessToken: !!accessToken,
                tokenLength: accessToken?.length || 0,
                clientId: MICROSOFT_INTEGRATION_CLIENT_ID,
              });
              window.opener?.postMessage(
                JSON.stringify({ type: MSFT_FILE_PICKER_CANCEL, reason: 'file picker cancelled' }),
                PARENT_ORIGIN,
              );
            },
            error: (error: any) => {
              console.error('[OneDrive SDK] File picker error:', error);
              window.opener?.postMessage(
                JSON.stringify({
                  type: MSFT_FILE_PICKER_ERROR,
                  error: String(error),
                  reason: `file picker error: ${error}`,
                }),
                PARENT_ORIGIN,
              );
            },
          });
        } catch (oneDriveError) {
          console.error('[OneDrive SDK] OneDrive.open() failed:', oneDriveError);
          window.opener?.postMessage(
            JSON.stringify({
              type: MSFT_FILE_PICKER_ERROR,
              error: String(oneDriveError),
              reason: `OneDrive.open() failed: ${oneDriveError}`,
            }),
            PARENT_ORIGIN,
          );
        } finally {
          window.removeEventListener('message', filterNonMicrosoftMessages, true);
        }
      } catch (error) {
        console.error('[OneDrive SDK] Failed to open file picker or updating status:', error);
        window.opener?.postMessage(
          JSON.stringify({
            type: MSFT_FILE_PICKER_ERROR,
            error: String(error),
            reason: `openOneDriveFilePicker error: ${error}`,
          }),
          PARENT_ORIGIN,
        );
      } finally {
        sessionStorage.removeItem(MSFT_FILE_PICKER_PENDING);
      }
      // TODO: Add cleanup window logic if needed. Ignoring such edge case for now
      // For edge case where postMessage fails and the parent window (DocumentSetUpload) is not able to close the popup window successfully
      // window.close();
    },
    [accessToken, acquireAccessToken, loadOneDriveSDK],
  );

  /**
   * Authenticates the user and then opens the OneDrive file picker
   */
  const authenticateAndOpenPicker = useCallback(async () => {
    console.log('[MSAL] Starting popup authentication...');
    await instance
      // 1. Get user account info
      // Let user select account and log in
      // Note: option to try to use silent authentication first, and if it fails, use popup authentication
      // Note: also we can default to the first account in the accounts array from MSAL instance instead of have user always select an account
      // Update if user experience is not good with account selection
      // Note: not adding this check for now since user may want to switch accounts
      .loginPopup(loginRequest)
      .then((authResult) => {
        const { account } = authResult;
        console.log('[MSAL] Popup authentication successful for account:', account.username);
        return account;
      })
      .catch((error) => {
        console.error('[MSAL] Failed to get user account info:', error);
        throw error;
      })
      .then(async (account) => {
        await openOneDriveFilePicker(account);
      });
  }, [instance, openOneDriveFilePicker]);

  useEffect(() => {
    // Prevent multiple executions
    if (msalInstanceRef.current) {
      console.log('[MSAL] MSAL Instance already initialized, skipping');
      return;
    }
    msalInstanceRef.current = true;

    const initializeAndStart = async () => {
      try {
        // when returning to the page from OAuth, we have to look in the query parameters,
        // and when returning to the page from the file picker, we have to look in the hash value
        if (window.location.search.includes('state') || window.location.hash.includes('state')) {
          console.log('[MSAL] Detected MSAL redirect hash, letting MSAL handleRedirectPromise…');
          await loadOneDriveSDK();
          await instance.initialize();
          await instance.handleRedirectPromise(); // Handle the redirect from MSAL
          return;
        }

        // Check if MSFT_FILE_PICKER_PENDING is set
        const pendingFlag = sessionStorage.getItem(MSFT_FILE_PICKER_PENDING);
        console.log('[MSAL] Checking session storage flag:', pendingFlag);

        // If MSFT_FILE_PICKER_PENDING is not set, it means this is not a file picker callback
        // This is likely a race condition or initialization issue - exit
        if (pendingFlag !== 'true') {
          console.log(`[MSAL] MSFT_FILE_PICKER_PENDING = ${pendingFlag}, exiting ...`);
          return;
        }

        // Ensure MSAL instance is properly initialized before proceeding
        console.log('[MSAL] Initializing MSAL instance...');
        await instance.initialize();
        console.log('[MSAL] MSAL instance initialized successfully');

        // Start MSAL + OneDrive File Picker flow
        await authenticateAndOpenPicker();
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        console.error('[MSAL] MSAL auth & file picker error:', errorMessage);
        window.opener?.postMessage(
          JSON.stringify({
            type: MSFT_FILE_PICKER_ERROR,
            error: `MSAL instance initialization error: ${errorMessage}`,
          }),
          PARENT_ORIGIN,
        );
      }
    };

    // Start MSAL + OneDrive File Picker flow
    initializeAndStart();
  }, [location.search, instance, authenticateAndOpenPicker, loadOneDriveSDK]);

  return (
    <div style={{ padding: '20px', textAlign: 'center' }}>
      <h2>Microsoft Authentication</h2>
      <p>Use Microsoft popup to authenticate and select files from Microsoft File Picker</p>
    </div>
  );
};
